//
// legalRevLoader
//
// Fetches the legal document versions LegalAcceptanceGate compares against, and
// keeps retrying until they arrive: with no versions the gate lets everyone
// through, so giving up would silently skip acceptance for the whole session.
//
import { useSyncExternalStore } from 'react';
import superagent from 'superagent';

import config from './config';
import {
  registerLoader,
  completeLoader,
} from './loadingRegistry';

import { logError } from './index';

const RETRY_MS = 30000;
const ALARM_EVERY = 20;

const loaderId = registerLoader('legalRev');

const listeners = new Set();

let legalRev = null;
let attempts = 0;

const subscribe = (onChange) => {
  listeners.add(onChange);
  return () => listeners.delete(onChange);
};

const getSnapshot = () => legalRev;

// Every attempt logs to the console; only some reach the backend, to keep an outage off Discord
const reportMissing = (text, code, info) => {
  if ((attempts === 1) || ((attempts % ALARM_EVERY) === 0)) {
    logError(text, code, info);
    return;
  }

  logError(text);
};

const fetchLegalRev = async () => {
  attempts += 1;

  try {
    const url = config.town.back.urlPrefix + config.town.back.legal.rev;
    const res = await superagent.get(url);
    const answer = res.body?.rslt?.data?.[0];

    if (answer?.legalUnavailable) {
      reportMissing(
        'legalRevLoader: back-town holds no legal documents',
        'legalRevLoader.unavailable',
        { attempts },
      );
    } else {
      legalRev = answer ?? null;
    }
  } catch (err) {
    reportMissing(
      `legalRevLoader: ${err.status ?? 'network error'}`,
      'legalRevLoader.fetch.error',
      {
        attempts,
        err: err?.message,
        ...(err?.status && { status: err.status }),
      },
    );
  }

  completeLoader(loaderId);

  if (!legalRev) {
    setTimeout(fetchLegalRev, RETRY_MS);
    return;
  }

  listeners.forEach((onChange) => onChange());
};

// Start fetching immediately on import
// Use void to make it clear we do not want to wait this async function:
void fetchLegalRev(); // NOSONAR top-level await would block app startup on this fetch

const useLegalRev = () => useSyncExternalStore(subscribe, getSnapshot);

export default useLegalRev;
